import { Controller } from "@hotwired/stimulus"
import Rails from "@rails/ujs"
import { debounce } from "lodash"

// Inline lookup GUS dla formularza "Dodawanie nowej firmy" (Panel Biura).
// W przeciwienstwie do dropdown_company_search (modal) NIP wpisuje sie wprost
// w formularzu, a po pobraniu danych pola nazwa/adres sa wypelniane i odslaniane.
// Reuzywa backend GUS: POST /clients/gus_data.
// Po pobraniu: sprawdza czy firma o tym NIP ma juz konto (ostrzezenie informacyjne)
// oraz sugeruje wolny adres konta (prefix) z nazwy firmy.
export default class extends Controller {
  static targets = [
    "nip", "gusButton", "loader", "error", "hint", "success", "duplicateWarning",
    "fields", "name", "street", "postCode", "city",
    "prefix", "prefixAvailable", "prefixTaken", "prefixInvalid", "prefixGroup", "prefixNormalized",
  ]

  static values = {
    endpointUrl: String,
    checkTaxNoUrl: String,
    suggestPrefixUrl: String,
    checkPrefixUrl: String,
    errorMessage: String,
    prefixMinLength: Number,
  }

  initialize() {
    // debounce na sprawdzanie prefixu przy recznej edycji pola
    this.checkPrefix = debounce(this.checkPrefix.bind(this), 400)
  }

  disconnect() {
    // Turbo Drive (Turbo.session.drive = true) podmienia body przy nawigacji, wiec formularz moze
    // zniknac w trakcie 400 ms debounce'u - bez cancel leci zbedny request, a jego callback siega
    // po prefixTarget, ktorego juz nie ma (Stimulus rzuca "Missing target element")
    this.checkPrefix.cancel()
  }

  fetchGusData() {
    // lookup w GUS to kilka sekund i kilka wywolan - kolejne klikniecia w trakcie ignorujemy
    const nip = this.nipTarget.value.trim()
    if (nip === "" || this.gusButtonTarget.disabled) return

    this.resetMessages()
    this.clearCompanyData()
    this.toggleLoader(true)

    Rails.ajax({
      type: "post",
      url: this.endpointUrlValue,
      dataType: "json",
      data: new URLSearchParams({ type: "nip", numer: nip }),
      success: (data) => {
        this.toggleLoader(false)
        if (Array.isArray(data.errors) && data.errors.length > 0) {
          this.setError(data.errors.join(" "))
          return
        }
        this.fill(data.results)
        this.fieldsTarget.classList.remove("hidden")
        this.showSuccess()
        this.checkDuplicate(nip)
        this.suggestPrefix(data.results?.name)
      },
      error: () => {
        this.toggleLoader(false)
        this.setError(this.errorMessageValue)
      },
    })
  }

  // Sprawdza czy firma o tym NIP ma juz konto - jesli tak, pokazuje informacje (bez blokady)
  checkDuplicate(nip) {
    Rails.ajax({
      type: "get",
      url: `${this.checkTaxNoUrlValue}?${new URLSearchParams({ tax_no: nip })}`,
      dataType: "json",
      success: (data) => {
        this.duplicateWarningTarget.classList.toggle("hidden", !data?.exists)
      },
      error: () => {
        // Sprawdzenie duplikatu jest informacyjne i nie blokuje - przy bledzie po prostu nie pokazujemy chmurki
        this.duplicateWarningTarget.classList.add("hidden")
      },
    })
  }

  // Sugeruje wolny adres konta (prefix) z nazwy firmy i wstawia do pola
  suggestPrefix(name) {
    if (!name) return // GUS moze nie zwrocic nazwy - wtedy nie ma z czego zrobic prefixu

    Rails.ajax({
      type: "get",
      url: `${this.suggestPrefixUrlValue}?${new URLSearchParams({ name })}`,
      dataType: "json",
      success: (data) => {
        if (!data?.prefix) return
        // Nadpisuje tylko puste pole albo nasza wlasna poprzednia sugestie. Wartosc wpisana
        // recznie (albo prefill po bledzie walidacji) to wybor usera i zostaje - inaczej
        // pobranie danych z GUS po wpisaniu adresu cicho podmienia mu prefix
        const current = this.prefixTarget.value.trim()
        if (current !== "" && current !== this.lastSuggestedPrefix) return

        this.prefixTarget.value = data.prefix
        this.lastSuggestedPrefix = data.prefix
        this.clearServerError()
        this.setPrefixStatus("available", data.prefix)
      },
    })
  }

  // Reczna edycja pola prefix - sprawdza dostepnosc (debounce w initialize)
  checkPrefix() {
    const prefix = this.prefixTarget.value.trim()
    if (prefix === "") {
      this.setPrefixStatus(null)
      return
    }

    // Normalizacja (Account.normalize_prefix) nigdy nie wydluza wartosci, wiec za krotki input
    // to pewny werdykt bez requestu. Za dlugiego nie rozstrzygamy lokalnie - po normalizacji
    // moze sie skrocic; oceni go backend
    if (prefix.length < this.prefixMinLengthValue) {
      this.clearServerError()
      this.setPrefixStatus("invalid")
      return
    }

    Rails.ajax({
      type: "get",
      url: `${this.checkPrefixUrlValue}?${new URLSearchParams({ prefix })}`,
      dataType: "json",
      // Odpowiedzi moga przyjsc nie po kolei - stan pola ustawia tylko odpowiedz na aktualna wartosc
      success: (data) => {
        if (this.prefixTarget.value.trim() !== prefix) return

        this.clearServerError()
        this.setPrefixStatus(data?.status, data?.normalized)
      },
      error: () => this.setPrefixStatus(null),
    })
  }

  // Zdejmuje marker bledu serwerowego (czerwona ramka + komunikat renderowane z backendu).
  // Wolane WYLACZNIE razem z werdyktem live-checku dla aktualnej wartosci pola - zdjecie
  // bledu bez zastepstwa zostawialoby pole wygladajace na poprawne
  clearServerError() {
    this.prefixGroupTarget.classList.remove("error")
  }

  // status: "available" | "taken" | "invalid"; null/undefined -> ukryj wszystkie.
  // normalized (przy "available") - wartosc po normalizacji serwerowej, pokazywana userowi,
  // zeby zielony werdykt dotyczyl adresu, ktory faktycznie powstanie ("Moja Firma" -> "moja-firma")
  setPrefixStatus(status, normalized = "") {
    if (status === "available") this.prefixNormalizedTarget.textContent = normalized
    this.prefixAvailableTarget.classList.toggle("hidden", status !== "available")
    this.prefixTakenTarget.classList.toggle("hidden", status !== "taken")
    this.prefixInvalidTarget.classList.toggle("hidden", status !== "invalid")
  }

  fill(results) {
    this.nameTarget.value = results?.name || ""
    this.streetTarget.value = results?.street || ""
    this.postCodeTarget.value = results?.post_code || ""
    this.cityTarget.value = results?.city || ""
  }

  showSuccess() {
    this.hintTarget.classList.add("hidden")
    this.successTarget.classList.remove("hidden")
  }

  // Dane firmy nie moga przezyc kolejnego pobrania: gdy drugi lookup sie nie uda, stara nazwa
  // i adres zostalyby wyslane razem z nowym NIP-em (oddzial z danymi firmy A i NIP-em firmy B)
  clearCompanyData() {
    this.fill(null)
    this.fieldsTarget.classList.add("hidden")
    this.setPrefixStatus(null)
  }

  // Czysci komunikaty przed nowym pobraniem
  resetMessages() {
    this.setError("")
    this.successTarget.classList.add("hidden")
    this.duplicateWarningTarget.classList.add("hidden")
    this.hintTarget.classList.remove("hidden")
  }

  toggleLoader(show) {
    this.loaderTarget.classList.toggle("hidden", !show)
    this.gusButtonTarget.disabled = show
  }

  setError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.toggle("hidden", message === "")
  }
}
